import {IconArrowRightExtraBold, IconCall, IconMap} from '@defign/asset'
import {BiImage, isCard} from '@financial/common-bi'
import classNames from 'classnames/bind'

import {무료보험_카테고리_아이콘} from '$constants/insurance/my/detail'
import {sendClickNlog} from '$utils/nlog'

import Styles from './InsuranceCompanyContactLayer.module.scss'

import type {무료보험_카테고리} from '$share/constants/insurance'
import type {ValueOf} from 'next/constants'
import type {PropsWithChildren} from 'react'

const cx = classNames.bind(Styles)

function LayerItem({
    title,
    url,
    children,
    nClkCode,
}: PropsWithChildren<{title: string; url?: string; nClkCode?: string}>) {
    const handleClick = () => {
        nClkCode && sendClickNlog(nClkCode)
    }

    if (!url) {
        return null
    }

    return (
        <li className={cx('item')}>
            <span className={cx('item-title')}>{title}</span>
            <span className={cx('item-value')}>
                <a href={url} className={cx('link')} onClick={handleClick}>
                    {children}
                </a>
            </span>
        </li>
    )
}

interface InsuranceCompanyContactLayerWrapperProps {
    orgCode?: string
    title: string
    freeInsuCategory?: ValueOf<typeof 무료보험_카테고리>
}
function InsuranceCompanyContactLayerWrapper({
    orgCode,
    title,
    children,
    freeInsuCategory,
}: PropsWithChildren<InsuranceCompanyContactLayerWrapperProps>) {
    const Icon = freeInsuCategory ? 무료보험_카테고리_아이콘[freeInsuCategory] : IconMap

    return (
        <div className={cx('article')}>
            <div className={cx('bi-image')} style={{width: 64, height: 64}}>
                {orgCode ? (
                    <BiImage
                        biId={{
                            mydataOrgCode: orgCode,
                        }}
                        type={
                            isCard({
                                mydataOrgCode: orgCode,
                            })
                                ? 'Plate'
                                : 'Profile'
                        }
                        id={`${title}${orgCode}`}
                        width={64}
                    />
                ) : (
                    <Icon width={64} height={64} fill={'#fff'} />
                )}
            </div>
            <h3 className={cx('title')}>{title}</h3>
            {children}
        </div>
    )
}

interface InsuranceCompanyContactLayerProps extends InsuranceCompanyContactLayerWrapperProps {
    nClkPrefix: string
    homeUrl: string
    serviceCenterTel: string
    serviceCenterUrl?: string
    claimUrl?: string
    accidentReportUrl?: string
    emergencyTel?: string
}
export function InsuranceCompanyContactLayer({
    orgCode,
    nClkPrefix,
    title,
    homeUrl,
    serviceCenterTel,
    serviceCenterUrl,
    claimUrl,
    accidentReportUrl,
    emergencyTel,
    freeInsuCategory,
}: InsuranceCompanyContactLayerProps) {
    return (
        <InsuranceCompanyContactLayerWrapper orgCode={orgCode} freeInsuCategory={freeInsuCategory} title={title}>
            <ul className={cx('list')}>
                <LayerItem title="공식 홈페이지" url={homeUrl} nClkCode={`insudetail.${nClkPrefix}pophome`}>
                    홈페이지 바로가기
                    <span className={cx('icon-arrow')}>
                        <IconArrowRightExtraBold width={7} height={10} fill="#1e1e23" />
                    </span>
                </LayerItem>
                <LayerItem title="오프라인 문의" url={serviceCenterUrl} nClkCode={`insudetail.${nClkPrefix}popoffline`}>
                    가까운 고객센터 찾기
                    <span className={cx('icon-arrow')}>
                        <IconArrowRightExtraBold width={7} height={10} fill="#1e1e23" />
                    </span>
                </LayerItem>
                <LayerItem
                    title="보험 문의"
                    url={serviceCenterTel && `tel:${serviceCenterTel}`}
                    nClkCode={`insudetail.${nClkPrefix}popcall`}
                >
                    <span className={cx('icon-call')}>
                        <IconCall width={15} height={15} fill="#09aa5c" id="insuranceContactServiceCenter" />
                    </span>
                    {serviceCenterTel}
                    <span className={cx('icon-arrow')}>
                        <IconArrowRightExtraBold width={7} height={10} fill="#1e1e23" />
                    </span>
                </LayerItem>
            </ul>
            {claimUrl && (
                <ul className={cx('list')}>
                    <LayerItem title="온라인 청구" url={claimUrl} nClkCode={`insudetail.${nClkPrefix}popclaim`}>
                        청구 방법 보기
                        <span className={cx('icon-arrow')}>
                            <IconArrowRightExtraBold width={7} height={10} fill="#1e1e23" />
                        </span>
                    </LayerItem>
                </ul>
            )}
            {(accidentReportUrl || emergencyTel) && (
                <ul className={cx('list')}>
                    <LayerItem
                        title="온라인 사고 접수"
                        url={accidentReportUrl}
                        nClkCode={`insudetail.${nClkPrefix}popacci`}
                    >
                        접수 방법 보기
                        <span className={cx('icon-arrow')}>
                            <IconArrowRightExtraBold width={7} height={10} fill="#1e1e23" />
                        </span>
                    </LayerItem>
                    <LayerItem
                        title="긴급 출동 접수"
                        url={emergencyTel && `tel:${emergencyTel}`}
                        nClkCode={`insudetail.${nClkPrefix}popemergen`}
                    >
                        <span className={cx('icon-call')}>
                            <IconCall width={15} height={15} fill="#09aa5c" id="insuranceContactEmergency" />
                        </span>
                        {emergencyTel}
                        <span className={cx('icon-arrow')}>
                            <IconArrowRightExtraBold width={7} height={10} fill="#1e1e23" />
                        </span>
                    </LayerItem>
                </ul>
            )}
        </InsuranceCompanyContactLayerWrapper>
    )
}
